import React from 'react';
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ProductDetail } from '@/api/models';
import { useProductDetailQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import type {
    ProductBranchScope,
    StockFiguresScope,
} from '../productFilters/branchScopeMeta';
import { useProductBranchScope } from '../productFilters/useProductBranchScope';
import { keepProductOrigin } from '../productOrigin';
import {
    readProductDetailBranch,
    writeProductDetailBranch,
} from './productDetailAddress';
import {
    useDraftPublish,
    type DraftPublisher,
} from './draftPanel/useDraftPublish';
import { readProductLoad, type ProductLoadStatus } from './productDetailMeta';
import {
    useVariantBarcodesForm,
    type VariantBarcodesEditor,
} from './useVariantBarcodesForm';

/** Whose stock the product details page counts (bos-app#119). */
export type ProductDetailBranch = {
    /** The "Stock for" control: an OrgAdmin's picker, a BranchAdmin's line, or nothing. */
    scope: ProductBranchScope;
    /** The branch the figures count, as the picker holds it; `null` is All branches. */
    branchId: string | null;
    /** The picker's change: the branch goes into the page address. */
    onChange: (branchId: string | null) => void;
    /** Whose stock the figures count, for the labels that name it. */
    stockFigures: StockFiguresScope;
};

export type UseProductDetailResult = {
    productId: string | null;
    /** `notFound` when the product no longer exists: no `error`, so no Try again (bos-app#136). */
    status: ProductLoadStatus;
    data: ProductDetail | null;
    /** A failure Try again can recover from; never a product that no longer exists. */
    error: ErrorResponse | null;
    isLoading: boolean;
    /**
     * The product is on screen and the figures under Variants are the last
     * branch's, while the picked branch's load.
     */
    isStockLoading: boolean;
    onRetry: () => void;
    expandedVariantIds: Set<string>;
    onToggleVariant: (variantId: string) => void;
    onEdit: () => void;
    branch: ProductDetailBranch;
    /** A variant's barcodes drawer and its save (bos-app#224). */
    barcodes: VariantBarcodesEditor;
    /** A draft's issues and its Publish (bos-app#225). */
    draft: DraftPublisher;
};

/**
 * Drives the product detail page. Reads `:productId` off the route, fetches the
 * full graph (`GET /products/{id}`), and owns the per-variant expand/collapse
 * state for the variants list. Exposes the standard data contract
 * (`data` / `error` / `isLoading` / `onRetry`) so the page reads like the
 * self-served tables, plus a `status` that tells a product that no longer
 * exists from a failure worth retrying (bos-app#136).
 *
 * The stock figures follow a branch (bos-app#119): the one in the page
 * address (`?branch=`), which a list scoped to a branch hands on, under the
 * list's rules — an OrgAdmin's is checked against the branch list first and
 * the request waits for it, a BranchAdmin always reads their own branch.
 * Picking a branch replaces the address rather than adding to the history,
 * so the navbar's Back still returns to the list.
 */
export const useProductDetail = (): UseProductDetailResult => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const productId = params.productId ?? null;
    const [searchParams, setSearchParams] = useSearchParams();
    const addressBranchId = readProductDetailBranch(searchParams);

    const { addressBranch, branchScope, stockFigures } =
        useProductBranchScope(addressBranchId);
    const branchId = addressBranch.branchId;

    const query = useProductDetailQuery(productId, {
        branchId,
        enabled: !addressBranch.isPending,
    });

    const [expandedVariantIds, setExpandedVariantIds] = React.useState<
        Set<string>
    >(() => new Set());

    const data: ProductDetail | null =
        query.data && query.data.success ? query.data.response : null;

    const barcodes = useVariantBarcodesForm({ productId, product: data });
    const draft = useDraftPublish({ productId, product: data });

    const load = readProductLoad(query.data);
    const error: ErrorResponse | null =
        load.status === 'error' ? load.error : null;

    const onToggleVariant = React.useCallback((variantId: string) => {
        setExpandedVariantIds((previous) => {
            const next = new Set(previous);
            if (next.has(variantId)) {
                next.delete(variantId);
            } else {
                next.add(variantId);
            }
            return next;
        });
    }, []);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    // The origin the page was opened from travels on to the edit form, and
    // survives the branch picker's replacing the entry, so a Delete there can
    // return to it (bos-app#137).
    const onEdit = React.useCallback(() => {
        if (productId) {
            navigate(PATH.PRODUCTS.BIND_EDIT(productId), {
                state: keepProductOrigin(location.state),
            });
        }
    }, [location.state, navigate, productId]);

    // Compared with the address, not the branch the page reads: a branch the
    // organisation no longer has reads as All branches, and picking All
    // branches then has to take it out of the address.
    const onBranchChange = React.useCallback(
        (next: string | null) => {
            if (next === addressBranchId) {
                return;
            }
            setSearchParams(
                (current) => writeProductDetailBranch(current, next),
                { replace: true, state: keepProductOrigin(location.state) },
            );
        },
        [addressBranchId, location.state, setSearchParams],
    );

    const branch = React.useMemo<ProductDetailBranch>(
        () => ({
            scope: branchScope,
            branchId,
            onChange: onBranchChange,
            stockFigures,
        }),
        [branchId, branchScope, onBranchChange, stockFigures],
    );

    return {
        productId,
        status: load.status,
        data,
        error,
        // While the address's branch is being checked the request waits, and
        // a waiting request isn't loading by TanStack's count.
        isLoading: query.isLoading || addressBranch.isPending,
        isStockLoading: query.isPlaceholderData,
        onRetry,
        expandedVariantIds,
        onToggleVariant,
        onEdit,
        branch,
        barcodes,
        draft,
    };
};
